import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { AUDIENCE_CONSENT_EVENT, AUDIENCE_VISITOR_KEY, audienceAllowed, audienceVisitor, existingAudienceVisitor, isAudiencePage, sendAudience } from '@/lib/audience';

// Sem listeners de cliques: basta a página estar visível. Nada é enviado sem consentimento.
export const MarketplaceAudienceTracker = () => {
  const location = useLocation();
  const { user, loading } = useAuth();
  const view = useRef(null);
  const session = useRef(null);
  const sequence = useRef(0);

  useEffect(() => {
    if (loading) return undefined;
    let disposed = false;
    let busy = false;
    let live = null;
    let acknowledged = false;
    const leave = () => {
      if (live) { sendAudience({ ...live, kind: 'leave' }); live = null; }
    };
    const tick = async () => {
      if (disposed) return;
      const consent = audienceAllowed();
      if (!consent) {
        // Retirar consentimento elimina os registos associados ao identificador atual.
        const previousId = existingAudienceVisitor();
        const old = live || (view.current && { ...view.current.payload, sequence: sequence.current }) || (previousId && {
          visitor_id: previousId, session_id: crypto.randomUUID(), view_id: crypto.randomUUID(), sequence: 1, path: '/',
        });
        if (old) sendAudience({ ...old, kind: 'forget', consent: false });
        live = null; view.current = null; acknowledged = false;
        try { localStorage.removeItem(AUDIENCE_VISITOR_KEY); } catch (_) {}
        return;
      }
      if (!isAudiencePage(location.pathname) || user?.is_marketplace_admin || user?.is_super_admin || document.visibilityState !== 'visible') {
        leave(); return;
      }
      try {
        // A app tem login apenas administrativo; não contar sessões autenticadas, mesmo durante restauro.
        if (localStorage.getItem('token')) { leave(); return; }
        if (busy) return;
        if (!live) {
          const visitor = audienceVisitor();
          if (!session.current) session.current = crypto.randomUUID();
          if (view.current?.key !== location.key || view.current?.payload.visitor_id !== visitor) {
            view.current = { key: location.key, payload: {
              visitor_id: visitor, session_id: session.current, view_id: crypto.randomUUID(),
              path: location.pathname, consent: true,
            } };
          }
          live = { ...view.current.payload, sequence: ++sequence.current };
          acknowledged = false;
        }
        busy = true;
        const sending = live;
        const result = await sendAudience({ ...sending, kind: acknowledged ? 'heartbeat' : 'view' });
        if (!disposed && live === sending && result.accepted) acknowledged = true;
      } catch (_) { /* A medição nunca impede usar o site. */ }
      finally { busy = false; }
    };
    const visibility = () => { if (document.visibilityState === 'visible') tick(); else leave(); };
    tick();
    const timer = window.setInterval(tick, 30000);
    window.addEventListener(AUDIENCE_CONSENT_EVENT, tick);
    window.addEventListener('storage', tick);
    window.addEventListener('pagehide', leave);
    window.addEventListener('pageshow', tick);
    document.addEventListener('visibilitychange', visibility);
    return () => {
      disposed = true; clearInterval(timer); leave();
      window.removeEventListener(AUDIENCE_CONSENT_EVENT, tick);
      window.removeEventListener('storage', tick);
      window.removeEventListener('pagehide', leave);
      window.removeEventListener('pageshow', tick);
      document.removeEventListener('visibilitychange', visibility);
    };
  }, [location.key, location.pathname, user?.is_marketplace_admin, user?.is_super_admin, loading]);
  return null;
};